import { Download, Share, X } from "lucide-react";
import { useEffect, useEffectEvent, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { usePwaInstall } from "@/hooks/use-pwa-install";

/**
 * Install affordance for the sidebar profile card. Chromium gets an
 * "Install app" button that opens the browser's install dialog; Safari on iOS
 * has no install API, so it gets a dismissible Share sheet instruction. Renders
 * nothing when the app is installed or the browser cannot install it.
 *
 * The entry removes itself in two ways, and in both the control a keyboard or
 * screen-reader user holds unmounts while it has focus, so focus would fall to
 * `<body>`: using a control (its click handler), and the browser reporting
 * `appinstalled` (installed from the address bar or the dialog while focus sat
 * on the button). `onSettled` lets the host move focus to a stable element
 * first. It runs only when the control held focus, so a pointer click in a
 * browser that does not focus buttons, or an install finished elsewhere, leaves
 * focus alone.
 */
export function InstallAppEntry({
  className,
  onSettled,
}: {
  className?: string;
  onSettled?: () => void;
}) {
  const { affordance, dismissIosHint, promptInstall } = usePwaInstall();
  const controlRef = useRef<HTMLButtonElement>(null);

  const settleIfFocused = useEffectEvent(() => {
    if (controlRef.current && document.activeElement === controlRef.current) {
      onSettled?.();
    }
  });

  useEffect(() => {
    // Capture phase, so this runs before the install hook's own listener on the
    // same target: that one notifies React, and the control must still be in the
    // DOM, with focus, when we look.
    const handleInstalled = () => settleIfFocused();
    window.addEventListener("appinstalled", handleInstalled, { capture: true });

    return () => window.removeEventListener("appinstalled", handleInstalled, { capture: true });
  }, []);

  const settleFrom = (control: HTMLElement) => {
    if (document.activeElement === control) {
      onSettled?.();
    }
  };

  if (affordance === "prompt") {
    return (
      <Button
        className={cn(
          "w-full justify-start rounded-lg text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring",
          className,
        )}
        onClick={(event) => {
          // The prompt is spent synchronously, so this button unmounts in the
          // same click; the host takes focus before React commits that.
          promptInstall().catch(() => undefined);
          settleFrom(event.currentTarget);
        }}
        ref={controlRef}
        size="mobileTouch"
        type="button"
        variant="ghost"
      >
        <Download aria-hidden="true" />
        Install app
      </Button>
    );
  }

  if (affordance === "ios-instructions") {
    return (
      <div
        aria-label="Install app"
        className={cn(
          "flex items-start gap-1 rounded-lg border border-sidebar-border/60 py-1 pr-1 pl-3 text-sidebar-foreground/85 text-xs",
          className,
        )}
        role="note"
      >
        <p className="min-w-0 flex-1 py-2 leading-snug">
          <span className="font-medium text-sidebar-foreground">Install Xan.</span> Tap{" "}
          <Share aria-hidden="true" className="inline size-3.5 align-text-bottom" /> Share, then Add
          to Home Screen.
        </p>
        <Button
          aria-label="Dismiss install hint"
          className="shrink-0 rounded-lg text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring"
          onClick={(event) => {
            dismissIosHint();
            settleFrom(event.currentTarget);
          }}
          ref={controlRef}
          size="mobileTouchIcon"
          type="button"
          variant="ghost"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      </div>
    );
  }

  return null;
}
